import { format, isBefore, isAfter, parseISO } from "date-fns";
import {
  ERROR,
  WARNING,
  ERROR_PRIMARY,
  WARNING_PRIMARY,
  GREY,
} from "@/config/mui/colorPalette";
import { SelectiveBookingLinkQueryResult } from "@/graphql/queries/onlineBooking/selectiveBookingLinks.graphql.types";
import { MedspaInfo } from "@/views/booking/bookingView";

type ColorScheme = {
  dateStatusColor: string;
  dateIconColor: string;
};

/**
 * Formats a date string into MM/dd/yyyy format
 * @param date - ISO date string
 */
export const formatDate = (date: string): string => {
  return format(parseISO(date), "MM/dd/yyyy");
};

/**
 * Determines the booking status based on start and end dates
 * @param start - Start date of booking period (ISO string)
 * @param end - End date of booking period (ISO string)
 * @returns 'expired' | 'upcoming' | 'active'
 */
export const getSelectiveBookingStatus = (
  start: string | null,
  end: string | null
): "expired" | "upcoming" | "active" => {
  const now = new Date();

  if (end && isBefore(new Date(end), now)) {
    return "expired";
  }
  if (start && isAfter(new Date(start), now)) {
    return "upcoming";
  }
  return "active";
};

/**
 * Returns color scheme based on current booking status
 * @param status - Booking status
 */
export const getColorByStatus = (
  status: "expired" | "upcoming" | "active"
): ColorScheme => {
  switch (status) {
    case "expired":
      return { dateStatusColor: ERROR[80], dateIconColor: ERROR_PRIMARY };
    case "upcoming":
      return { dateStatusColor: WARNING[80], dateIconColor: WARNING_PRIMARY };
    default:
      return { dateStatusColor: GREY[90], dateIconColor: GREY[50] };
  }
};

/**
 * Creates a human-readable date range string
 * @param start - Start date of booking period (ISO string)
 * @param end - End date of booking period (ISO string)
 */
export const formatDateRange = (
  start: string | null,
  end: string | null
): string => {
  if (!start && !end) return "";
  if (!start) return `Bookable until ${formatDate(end!)}`;
  if (!end) return `Bookable from ${formatDate(start)}`;
  return `Bookable ${formatDate(start)} - ${formatDate(end)}`;
};

/**
 * Checks if a selective booking link is invalid or expired
 * A selective booking link is invalid when:
 * - The link is expired: Date range is before current date
 * - The link has no eligible providers: No providers are eligible for the service
 * - The link has no service: No service is associated with the link
 */
export const isInvalidSelectiveBookingLink = (
  isSelectiveBookingLink: boolean,
  selectiveBookingLink: SelectiveBookingLinkQueryResult["data"]["selectiveBookingLinkByPk"],
  services: MedspaInfo["serviceMenuItems"]
): boolean => {
  // If we don't have a selective booking link, we don't need to check anything
  if (!isSelectiveBookingLink) {
    return false;
  }

  if (!selectiveBookingLink) {
    return true; // Invalid if we have link param but no data
  }

  const isExpired =
    getSelectiveBookingStatus(
      selectiveBookingLink.startDate || null,
      selectiveBookingLink.endDate || null
    ) === "expired";

  if (isExpired) {
    return true;
  }

  // A selective booking link can only have one service
  const serviceId = selectiveBookingLink.services[0].id;
  const service = services.find((service) => service.id === serviceId);

  if (!service) {
    return true; // Invalid if service not found
  }

  const eligibleProviderIds = service.eligibleProviders.map(
    (provider) => provider.userMedspa.id
  );

  const hasEligibleProvider = selectiveBookingLink.providers.some((provider) =>
    eligibleProviderIds.includes(provider.userMedspaId)
  );

  return !hasEligibleProvider;
};
